import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/**
 * EmptyState
 * Classification: custom
 *
 * Domain-specific empty/zero-data placeholder with an illustration slot, title,
 * description and an optional primary action. Used for empty tables, no search
 * results, and first-run states.
 */
export interface EmptyStateProps {
  title: string;
  description?: string;
  /** Illustration or icon. */
  icon?: ReactNode;
  /** Primary action (e.g. a Button). */
  action?: ReactNode;
  dense?: boolean;
}

export function EmptyState({ title, description, icon, action, dense = false }: EmptyStateProps) {
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.5,
        py: dense ? 4 : 8,
        px: 3,
        color: 'text.secondary',
      }}
    >
      {icon ? (
        <Box sx={{ fontSize: dense ? 40 : 56, lineHeight: 0, color: 'text.disabled' }}>{icon}</Box>
      ) : null}
      <Typography variant={dense ? 'subtitle1' : 'h6'} color="text.primary">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body1" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      ) : null}
      {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
    </Box>
  );
}
